import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { DeleteOutline as Delete, Done } from '@mui/icons-material';

import { FHChip, FHConfirmDialog } from '../../../base';
import { ClaimStatusColor, ClaimStatusFontColor } from './constants';
import { FORTA_BLUE, FORTA_ORANGE, FORTA_SUCCESS, FORTA_WHITE } from '../../../../utils/colors';
import { formatEntityId, history, usDollarFormatter } from '../../../../helpers';
import { ENTITY_PREFIX } from '../../../../constants';
import { claimsActions } from '../../../../store';
import { ClaimSourceLabels } from '../../../../pages/constants';

import type { RootState } from '../../../../store';
import type { IClaim } from '../../../../utils/types';

const ClaimHeaderNoDataEnum = {
  Status: '#E2E2E2',
  StatusFont: '#6E6F71',
  CHId: '#DAECFE',
  Amount: '#E5F4E0',
};

interface IClaimInfoChipsProps {
  dataTestIdPrefix?: string;
  showClaimLink?: boolean;
  showDeleteButton?: boolean;
  claim: IClaim | null;
}

function ClaimInfoChips({
  dataTestIdPrefix,
  claim,
  showClaimLink = false,
  showDeleteButton = false,
}: IClaimInfoChipsProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { loading: claimLoading } = useSelector((state: RootState) => state.claims);
  const [showDeleteClaimDialog, setShowDeleteClaimDialog] = useState<boolean>(false);

  const handleDeleteClaim = async (claimId: number) => {
    if (!claimId) return;
    setShowDeleteClaimDialog(false);
    const res = await dispatch(
      claimsActions.deleteClaim({
        claimId,
        getAccessTokenSilently,
      }),
    );
    if (!res.error) {
      history.navigate('/dashboard');
    }
  };

  return (
    <Box
      key={claim?.id}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      data-testid={dataTestIdPrefix}
    >
      {showClaimLink && (
        <Typography
          onClick={() => {
            history.navigate(`/billings/claims/${claim?.id}`);
          }}
          textAlign="left"
          fontWeight={700}
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
            display: 'inline',
            alignSelf: 'flex-start',
          }}
        >
          {formatEntityId(claim?.id ?? '', ENTITY_PREFIX.Claim)}
        </Typography>
      )}
      <Stack direction={'row'} spacing={1} my={2}>
        <FHChip
          content={claim?.status ? claim?.status : 'Claim status'}
          bgColor={claim?.status ? ClaimStatusColor[claim?.status] : ClaimHeaderNoDataEnum.Status}
          color={
            claim?.status ? ClaimStatusFontColor[claim?.status] : ClaimHeaderNoDataEnum.StatusFont
          }
        />
        {claim?.is_invoiceable && !claim?.invoice_id && (
          <FHChip content={'Ready to invoice'} bgColor={FORTA_ORANGE} color={FORTA_WHITE} />
        )}
        {Boolean(claim?.invoice_id) && (
          <FHChip
            contentRenderer={() => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: FORTA_WHITE,
                  backgroundColor: FORTA_SUCCESS,
                  px: 2,
                  py: 0.5,
                  borderRadius: 4,
                }}
              >
                <Typography sx={{ mr: 1 }}>Invoiced</Typography>
                <Done
                  sx={{
                    color: FORTA_WHITE,
                    fontSize: 18,
                  }}
                />
              </Box>
            )}
          />
        )}
        <FHChip
          content={
            claim?.clearing_house_claim_id
              ? 'CH ID ' + claim?.clearing_house_claim_id
              : 'Clearinghouse claim ID'
          }
          sx={{
            cursor: 'pointer',
            border: '1.5px dashed',
            fontWeight: 700,
          }}
          bgColor={FORTA_WHITE}
          color={FORTA_BLUE.primary}
        />
        <FHChip
          content={
            claim?.form_1500_data.total_charge
              ? usDollarFormatter.format(claim?.form_1500_data.total_charge || 0)
              : 'Amount billed not set'
          }
          sx={{
            cursor: 'pointer',
            border: '1.5px dashed',
            fontWeight: 700,
          }}
          bgColor={FORTA_WHITE}
          color={FORTA_BLUE.primary}
        />

        {claim?.source && (
          <FHChip
            content={claim?.source ? `Source: ${ClaimSourceLabels[claim?.source]}` : ''}
            bgColor={FORTA_ORANGE}
            color={FORTA_WHITE}
          />
        )}
      </Stack>
      {showDeleteButton && (
        <IconButton
          sx={{
            width: 150,
            height: 36,
            border: `2px solid ${FORTA_BLUE.primary}`,
            borderRadius: '20px',
            backgroundColor: 'transparent',
            gap: 1,
          }}
          disabled={claimLoading}
          onClick={() => setShowDeleteClaimDialog(true)}
        >
          <Delete width={16} sx={{ color: FORTA_BLUE.primary }} />
          <Typography
            sx={{
              color: FORTA_BLUE.primary,
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            Delete claim
          </Typography>
        </IconButton>
      )}
      <FHConfirmDialog
        title={'Delete claim'}
        open={showDeleteClaimDialog}
        sx={{
          '& .MuiModal-backdrop': {
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
          },
        }}
        submittext={'Delete'}
        canceltext="Cancel"
        content={`Are you sure you want to delete claim ${formatEntityId(
          claim?.id ?? '',
          ENTITY_PREFIX.Claim,
        )}?`}
        onClose={(_, reason) => {
          if (reason && reason === 'backdropClick') return;
          setShowDeleteClaimDialog(false);
        }}
        onSubmit={() => {
          handleDeleteClaim(claim?.id ?? 0);
        }}
      />
    </Box>
  );
}

export default ClaimInfoChips;
